import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useGet360AnalysisQuery } from '../data-access/project.api';
import { selectProjectResults } from '../data-access/project.reducer';
import BiasLLMPlotsSection from '../../Bias/BiasLLMPlotsSection';
import { FairnessCard } from '../../Dashboard/Fairness/FairnessCard';
import ExplainabilityCard from '../../Dashboard/Explainability-ML/ExplainabilityCard';
import { AISafetyDetection } from '../../AISafety/AISafetyDetection';
import { PromptInjectionPDC } from '../../PromptInjectionPDC/PromptInjectionPDC';
import { getLatestPDCRunFromProjectResults, resolve360PDCRun } from '../../PromptInjectionPDC/pdc.utils';
import { coalesce360Section } from '../../shared/complianceIq360.utils';

interface Review360TabProps {
  projectId?: string;
}

const Review360Tab: React.FC<Review360TabProps> = ({ projectId }) => {
  // Fetch 360-aggregated data whenever this tab is rendered (keepUnusedDataFor: 0)
  const {
    data: analysis360,
    isLoading,
    isError,
    error,
    refetch
  } = useGet360AnalysisQuery(projectId!, {
    skip: !projectId
  });

  const projectResults = useSelector(selectProjectResults);

  const detailedData = analysis360?.aggregated_results?.detailed_360_data;
  const bias360 = coalesce360Section(detailedData?.bias);
  const fairness360 = coalesce360Section(detailedData?.fairness);
  const explainability360 = coalesce360Section(detailedData?.explainability);
  const aiSafety360 = coalesce360Section(detailedData?.ai_safety) ?? coalesce360Section(detailedData?.abuse_ai);

  const pdc360 = useMemo(
    () => resolve360PDCRun(analysis360) ?? getLatestPDCRunFromProjectResults(projectResults as Record<string, unknown>),
    [analysis360, projectResults]
  );

  return (
    <Box sx={{ padding: '24px 32px' }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Compliance IQ Analysis Overview
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
        Comprehensive view of all analysis metrics aggregated by the latest Analysis
      </Typography>

      {/* Loading state */}
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Fetching aggregated Compliance IQ analysis…
          </Typography>
        </Box>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Typography
              variant="body2"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => refetch()}
            >
              Retry
            </Typography>
          }
        >
          Could not load Compliance IQ aggregated data.{' '}
          {(error as any)?.data?.error || 'Showing per-tab data as fallback below.'}
        </Alert>
      )}

      {/* Bias Detection */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          Bias Detection
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {/* BiasLLMPlotsSection drives from Redux (bias has its own complex charting) */}
        <BiasLLMPlotsSection customSummary={bias360} />
      </Box>

      {/* Fairness Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          Fairness Analysis
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <FairnessCard customData={fairness360} />
      </Box>

      {/* Explainability Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          Explainability Analysis
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <ExplainabilityCard customData={explainability360} />
      </Box>

      {/* AI Safety Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          AI Safety Detection
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <AISafetyDetection customData={aiSafety360} showDashboardKpis={false} showInlineVersionControls={false} />
      </Box>

      {/* Prompt Injection Detection Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          Prompt Injection Detection
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <PromptInjectionPDC customData={pdc360} showDashboardKpis={false} showInlineVersionControls={false} />
      </Box>
    </Box>
  );
};

export default Review360Tab;
